/**
 * tracking-lib.client.ts — init @nhst/tracking-lib and fire trackPageLoad per navigation.
 *
 * page.title / page.data_layout are passed EXPLICITLY (from the cpp:data:loaded payload), not
 * auto-filled from the DOM: on SPA navigation unhead (Nuxt 3.17) patches document.title a few
 * microtasks after cpp:data:loaded fires, so a DOM read here would yield the PREVIOUS page's
 * title. We pass the clean pre-titleTemplate title (matching legacy, which strips the postfix)
 * so it stays deterministic. data_layout only matters on articles (only article.vue sets the
 * datalayout attr).
 *
 * Library auto-fills the rest (event, timestamp, instance_/agent_/consent_, user_one_id, url,
 * previous_page_*, visibility, mode_, auto_refresh, ...). CPP supplies: producer_contract v2,
 * app name/version, page.type, page.publication, page.title, page.data_layout, the article
 * block, attributes (edition_id/name), and a UA-derived container_app fallback.
 *
 * Legacy dual-emit (window.nhstDataLayer) is handled by plugins/tracking-lib-directive.ts.
 */
import { initializeTracking, trackPageLoad, type TrackingEvent } from '@nhst/tracking-lib';
import { useArticleStore } from '~/store/article';
import { type Article } from '~/types/article';
import useSkipModules from '~/composables/useSkipModules';
import { getArticleStats } from '~/composables/useArticleStats';
import { mapPageType, mapWallType } from '~/utils/trackingMappers';

type TrackingPage = NonNullable<TrackingEvent['page']>;
type TrackingArticle = NonNullable<TrackingEvent['article']>;

/*
 * UA fallback for native wrappers (e.g. "...DNApp/1.2.0 Android") when they don't inject
 * window.__CONTAINER_APP__ — otherwise agent_surface misclassifies them as 'web'.
 */
function detectContainerApp (ua: string): { name: string | undefined; version: string | undefined } {
  const match = ua.match(/(\w+)\/([\w.]+)\s(?:Android|iOS)/);
  return match ? { name: match[1], version: match[2] } : { name: undefined, version: undefined };
}

/*
 * Post-merge sanitizer — see initializeTracking call site for rationale.
 * Exported for unit testing.
 */
export function dedupeArraysInterceptor (event: Record<string, unknown>): Record<string, unknown> {
  const article = event.article as Record<string, unknown> | undefined;
  const articleProps = article?.properties as Record<string, unknown> | undefined;
  if (articleProps) {
    if (Array.isArray(articleProps.authors)) {
      articleProps.authors = Array.from(new Set(articleProps.authors as string[]));
    }
    if (Array.isArray(articleProps.tags)) {
      articleProps.tags = Array.from(new Set(articleProps.tags as string[]));
    }
  }
  const content = event.content as Record<string, unknown> | undefined;
  const contentProps = content?.properties as Record<string, unknown> | undefined;
  if (contentProps && Array.isArray(contentProps.tags)) {
    contentProps.tags = Array.from(new Set(contentProps.tags as string[]));
  }
  return event;
}

/*
 * title = clean pre-titleTemplate title; data_layout = article datalayout (articles only).
 * Passed explicitly so the library never reads the un-flushed DOM (see file header).
 */
type CppDataLoadedPayload = { type: string; name?: string; title?: string; data_layout?: string };

export default defineNuxtPlugin((nuxt) => {
  const { skipModule, moduleNames } = useSkipModules(nuxt._route);
  if (skipModule(moduleNames.ADOBE)) { return; }

  const rtConfPub = useRuntimeConfig().public;
  const container = detectContainerApp(navigator.userAgent);

  /*
   * vueApp intentionally NOT passed: tracking-lib-directive.ts owns directive registration so
   * both nhstDataLayer and the new lib fire from one listener. When legacy is retired, pass
   * `vueApp: nuxtApp.vueApp` and delete that directive.
   */
  initializeTracking({
    publication: (rtConfPub.name as string) || '',
    env: (rtConfPub.appEnv as string) || 'test',
    applicationName: 'cpp',
    applicationVersion: (rtConfPub.appVersion as string) || '',
    producerContract: { name: 'cpp', version: '2', owner_type: 'application' },
    containerAppName: container.name,
    containerAppVersion: container.version,
    enableThemeTracking: true,
    /*
     * Dedup arrays the library's merge() double-concatenates (our context is spread into both
     * root context and event). Dedups authors/tags; inline_content has legit duplicates.
     */
    interceptEvents: dedupeArraysInterceptor,
    enableActiveTimeTracking: true
  });

  nuxt.hook('cpp:data:loaded', (payload: CppDataLoadedPayload) => {
    trackPageLoad(buildPageLoadPayload(payload.type, rtConfPub, payload.name, payload.title, payload.data_layout));
  });
});

/*
 * ---------------------------------------------------------------------------
 * Helpers (exported for unit testing)
 * ---------------------------------------------------------------------------
 */

/*
 * Page types whose name slug is emitted as attributes.name. searchpage ('search') and
 * staticpage are excluded (junk value / different key).
 */
const NAMED_PAGE_TYPES = ['sectionpage', 'categorypage', 'tagpage', 'latestpage'];

export function buildPageLoadPayload (
  type: string,
  rtConfPub: Record<string, any>,
  name?: string,
  title?: string,
  dataLayout?: string
): TrackingEvent {
  const page: TrackingPage = {
    type: mapPageType(type),
    publication: (rtConfPub.name as string) || '',
    ...(title ? { title } : {}),
    // Always explicit so the lib never reads the stale documentElement[datalayout] on SPA nav.
    data_layout: dataLayout || 'default'
  };

  // producer_contract v2: attributes carries only page-metadata (edition_id on frontpage, name on section/topic); omitted otherwise.
  const payload: TrackingEvent = { page };

  if (type === 'article') {
    buildArticlePayload(page, payload);
  }

  if (type === 'frontpage') {
    buildFrontpagePayload(payload, rtConfPub);
  }

  if (name && NAMED_PAGE_TYPES.includes(type)) {
    payload.attributes = { ...payload.attributes, name };
  }

  return payload;
}

export function buildArticlePayload (page: TrackingPage, payload: TrackingEvent): void {
  const article = useArticleStore().currentArticle as Article;
  if (!article?.id) { return; }

  const dayjs = useDayjs();

  const isWalled = (article.body?.filter(i => !i.injected).length || 0) < (article?.bodyLength || 0);

  page.availability = article.accessLevel === 'paywall' ? 'plus' : 'free';
  page.wall = isWalled;
  page.wall_type = mapWallType(article.accessLevel);

  const { wordCount, characterCount } = getArticleStats(article);
  const mainImage = useArticle().getMetaImage()?.src;

  /*
   * Coerce absent/'' values to undefined so the library's fillWithNulls sets `null` (per
   * contract); an empty string would defeat that default.
   */
  const articlePayload: TrackingArticle = {
    id: article.id,
    title: article.originalTitle || article.title,
    properties: {
      publication: article.publication?.name || undefined,
      publish_timestamp: dayjs(article.firstPublishedAt || article.publishedAt).valueOf(),
      modify_timestamp: dayjs(article.updatedAt || article.firstPublishedAt).valueOf(),
      word_count: wordCount,
      character_count: characterCount,
      category: article.categories?.[0]?.title || undefined,
      topic: article.topic?.name || undefined,
      tags: article.tags?.map(i => i.name),
      // Authors may repeat; final dedup is enforced post-emit by dedupeArraysInterceptor.
      authors: article.byline?.authors?.map(i => i.name),
      data_layout: article.dataLayout || undefined,
      lifespan_score: article.metadata?.levetid || undefined,
      news_score: article.metadata?.nyhetsverdi || undefined,
      no_list: article.metadata?.excludeFromStreams ? 'exclude' : 'include'
    }
  };

  if (mainImage && mainImage.length > 0 && articlePayload.properties) {
    articlePayload.properties.image = mainImage;
  }

  payload.article = articlePayload;
}

export function buildFrontpagePayload (payload: TrackingEvent, rtConfPub: Record<string, any>): void {
  const pathname = new URL(window.location.href).pathname;
  const editionId = pathname === '/'
    ? rtConfPub?.front?.options?.id
    : pathname === '/front'
      ? rtConfPub?.front?.options?.sandBox?.id
      : '';

  if (editionId) {
    payload.attributes = { ...payload.attributes, edition_id: editionId };
  }
}
